import React from 'react';
import { Box, Checkbox, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { S3FileItem } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import { formatBytes } from './SCUIS3Browser.helpers';
import type { CtxTarget } from './S3BrowserContextMenu';

export type S3FileListRowProps = {
    file: S3FileItem;
    isSelected: boolean;
    isPreviewRow: boolean;
    rowClickablePreview: boolean;
    fileSelectionEnabled: boolean;
    loading: boolean;
    showPreviewPanel: boolean;
    onPreviewClick: (file: S3FileItem) => void;
    onToggleSelect: (fileKey: string) => void;
    onContextMenu: (e: React.MouseEvent, target: CtxTarget) => void;
};

export const S3FileListRow: React.FC<S3FileListRowProps> = ({
    file,
    isSelected,
    isPreviewRow,
    rowClickablePreview,
    fileSelectionEnabled,
    loading,
    showPreviewPanel,
    onPreviewClick,
    onToggleSelect,
    onContextMenu,
}) => {
    const { t } = useTranslation();

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!showPreviewPanel || loading) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPreviewClick(file);
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onToggleSelect(file.key);
    };

    return (
        <ListItem disablePadding>
            <Box
                role={rowClickablePreview ? 'button' : undefined}
                tabIndex={rowClickablePreview ? 0 : -1}
                onKeyDown={handleKeyDown}
                onClick={() => onPreviewClick(file)}
                sx={(theme) => ({
                    display: 'flex',
                    width: '100%',
                    alignItems: 'flex-start',
                    py: 0.5,
                    pr: 1,
                    pl: fileSelectionEnabled ? 0.5 : 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderLeft: isPreviewRow
                        ? `3px solid ${theme.palette.primary.main}`
                        : '3px solid transparent',
                    bgcolor:
                        fileSelectionEnabled && isSelected
                            ? alpha(theme.palette.primary.main, 0.08)
                            : 'transparent',
                    opacity: loading ? 0.55 : 1,
                    cursor: rowClickablePreview ? 'pointer' : 'default',
                })}
                onContextMenu={(e) => onContextMenu(e, { kind: 'file', item: file })}
            >
                {fileSelectionEnabled && (
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.25 }}>
                        <Checkbox
                            edge="start"
                            checked={isSelected}
                            onChange={handleCheckboxChange}
                            onClick={(e) => e.stopPropagation()}
                            disabled={loading}
                            tabIndex={-1}
                            disableRipple
                            slotProps={{
                                input: {
                                    'aria-label': t('aiPrompt.s3Browser.selectFileForDownloadAria'),
                                },
                            }}
                        />
                    </ListItemIcon>
                )}
                <ListItemIcon sx={{ minWidth: 40, mt: 0.25 }}>
                    <InsertDriveFileIcon color="action" />
                </ListItemIcon>
                <ListItemText
                    primary={file.name}
                    secondary={`${file.key} · ${formatBytes(file.size)}${
                        file.lastModified ? ` · ${file.lastModified}` : ''
                    }`}
                />
            </Box>
        </ListItem>
    );
};
